/**
 * The programme buffer's raw writes, for the edit session alone, with the
 * test helpers built on them.
 *
 * Editor code changes the programme through `useEditSession()` (`edit` and
 * `applyRemote` in `@/platform/editor-sync-context`), which writes here and
 * decides the save in the same call. A write from anywhere else changes what
 * is on screen with nothing to send it, so eslint keeps this module out of
 * reach of everything but the session, the store and tests.
 *
 * @module state/programme-buffer
 */

import {
  defaultProgramme,
  focusStepForMode,
  importedProgramme,
  type ProgrammeUpdate,
  resetEditorNavigation,
  state,
  useEditorStore,
  withMode,
} from "@/state/store";
import type { Programme } from "@/types";

/** A partial merged into the programme, or an Immer recipe over it. */
export function writeProgramme(update: ProgrammeUpdate): void {
  useEditorStore.setState((draft) => {
    if (typeof update === "function") {
      update(draft.programme);
    } else {
      Object.assign(draft.programme, update);
    }
  });
}

/**
 * The programme replaced outright. An object skips Immer, so it is stored
 * unfrozen until the next recipe freezes it.
 */
export function replaceProgramme(programme: Programme): void {
  useEditorStore.setState({ programme });
}

/** Replace the buffer with an imported document, as opening a programme does. */
export function applyImportedProgramme(migrated: object): void {
  replaceProgramme(importedProgramme(migrated));
}

/**
 * Empties the editing buffer and returns navigation to the first step, without
 * saving: a test's fresh start. A reader's reset is an edit through the session.
 */
export function resetProgramme(): void {
  replaceProgramme(defaultProgramme());
  resetEditorNavigation();
}

/** Switches the mode on the editing buffer directly, for tests and the console. */
export function setMode(mode: string, assignedModuleIds: string[] = []): void {
  if (!state.programme) {
    console.error("Programme not loaded yet.");
    return;
  }
  if (mode !== "PROGRAMME_OWNER" && mode !== "MODULE_EDITOR") {
    console.error("Invalid mode. Use PROGRAMME_OWNER or MODULE_EDITOR");
    return;
  }
  writeProgramme(withMode(mode, assignedModuleIds));
  focusStepForMode(mode);
}
